import React from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useCare } from '../../hooks/useCare'
import { useInviteLink } from '../../hooks/useInviteLink'
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../../config/firebase'
import { getDefaultPermissions } from '../../types/user'

interface InviteFlowDebugProps {
  autoJoinAttempted?: boolean
  resetAutoJoinAttempted?: () => void
}

const InviteFlowDebug: React.FC<InviteFlowDebugProps> = ({ 
  autoJoinAttempted = false, 
  resetAutoJoinAttempted 
}) => {
  const { user, loading: authLoading } = useAuth()
  const { currentCase, loading: careLoading, loadUserCases } = useCare()
  const { inviteCode, isFromUrl, isProcessing, clearInvite } = useInviteLink()

  if (!inviteCode && !isFromUrl) return null

  const debugData = {
    user: {
      uid: user?.uid,
      email: user?.email,
      displayName: user?.displayName,
      exists: !!user
    },
    auth: {
      loading: authLoading
    },
    care: {
      currentCase: {
        id: currentCase?.id,
        name: currentCase?.name,
        exists: !!currentCase
      },
      loading: careLoading
    },
    invite: {
      code: inviteCode,
      isFromUrl,
      isProcessing
    },
    autoJoin: {
      attempted: autoJoinAttempted
    }
  }

  const manualAutoJoin = async () => {
    if (!user?.uid || !inviteCode) {
      console.log('❌ Cannot execute manual auto-join: missing user or invite code')
      return
    }

    console.log('🔄 Manual auto-join triggered for code:', inviteCode)

    try {
      // 1. Buscar el caso por código de invitación
      const casesQuery = query(
        collection(db, 'care-cases'),
        where('inviteCode', '==', inviteCode),
        where('isActive', '==', true)
      )

      const casesSnapshot = await getDocs(casesQuery)
      
      if (casesSnapshot.empty) {
        console.error('❌ Código de invitación inválido:', inviteCode)
        return
      }

      const caseDoc = casesSnapshot.docs[0]
      const caseData = caseDoc.data()
      
      console.log('✅ Caso encontrado:', caseData.name)

      // 2. Verificar si el usuario ya es miembro
      const membersQuery = query(
        collection(db, 'case-members'),
        where('userId', '==', user.uid),
        where('caseId', '==', caseDoc.id),
        where('isActive', '==', true)
      )

      const membersSnapshot = await getDocs(membersQuery)
      
      if (!membersSnapshot.empty) {
        console.log('✅ Usuario ya es miembro del caso')
        clearInvite()
        await loadUserCases()
        return
      }

      // 3. Agregar al usuario como miembro del caso
      const membershipData = {
        userId: user.uid,
        caseId: caseDoc.id,
        role: 'caregiver',
        permissions: getDefaultPermissions('caregiver'),
        joinedAt: serverTimestamp(),
        invitedBy: caseData.adminId,
        isActive: true,
        nickname: user.displayName || user.email?.split('@')[0] || 'Nuevo Cuidador',
        notes: `Se unió manualmente usando debug panel: ${inviteCode}`
      }

      await addDoc(collection(db, 'case-members'), membershipData)
      
      console.log('✅ Usuario añadido como miembro del caso')

      // 4. Limpiar el código de invitación y recargar casos
      clearInvite()
      await loadUserCases()

      console.log('🎉 Manual auto-join completado exitosamente')

    } catch (error) {
      console.error('❌ Error en manual auto-join:', error)
    }
  }

  return (
    <div className="fixed top-4 right-4 bg-black bg-opacity-75 text-white p-4 rounded-lg text-xs max-w-sm z-50">
      <h3 className="font-bold text-yellow-400 mb-2">🔍 Invite Flow Debug</h3>
      <pre className="text-xs overflow-auto max-h-48 mb-3">
        {JSON.stringify(debugData, null, 2)}
      </pre>
      
      {user?.uid && inviteCode && !currentCase && (
        <div className="space-y-2">
          <button
            onClick={manualAutoJoin}
            className="w-full bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
          >
            🚀 Force Auto-Join
          </button>
          
          {autoJoinAttempted && resetAutoJoinAttempted && (
            <button
              onClick={resetAutoJoinAttempted}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
            >
              🔄 Reset Auto-Join Flag
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default InviteFlowDebug 